import React, { useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import { userStore, generalStore, playlistStore } from '../../store';
import Helpers from '../../js/helpers';
import { useNavigate } from 'react-router';

import PlaylistItemsList from '../playlistItemsList/PlaylistItemsList';
import GoBackButton from '../goBackButton/GoBackButton';
import DeviceAssignmentModal from '../deviceAssignmentModal/DeviceAssignmentModal';

import { useTranslation } from 'react-i18next';
import { apiDelete, apiGet, apiPatch } from '../../js/api';
import Server from '../../js/config/_server';
import { RESPONSE, ROUTES, RolesOptions } from '../../js/_constants';
import DeleteConfirmationModal from '../deleteConfirmationModal/DeleteConfirmationModal';
import EditPlaylist from './EditPlaylist';
import EditPlaylistDealershipGroups from './EditPlaylistDealershipGroups';
import EditFallback from './editFallback';
import FallbackCountries from '../fallbackCountries/FallbackCountries';
import {
    type Playlist,
    type Device,
    type ModuleType,
    type Dealership
} from '../../api/types/index';
import { type ResourceResponse } from '../../api/types/response';
import IconButton from '../iconButton/IconButton';
import api from '../../api';
import { isNotEmpty, getIri } from '../../js/fpUtils';
import Loader from '../loader/Loader';
import Accordion from '../accordion/Accordion';
import { Link } from 'react-router';
import { getDealershipGroupLabel, type DealershipGroupLabel } from '../../js/dealershipGroupLabel';

import addIconPath from '@icons/add-small.svg';
import deleteIconPath from '@icons/trash.svg';
import duplicateIconPath from '@icons/duplicate.svg';
import assignIconPath from '@icons/sync-small.svg';

import './PlaylistContent.scss';

type PlaylistContentProps = {
    playlistId: string;
    playlist: Playlist;
    moduleTypes: ModuleType[];
};

export const PlaylistContent = ({
    playlistId,
    playlist,
    moduleTypes
}: PlaylistContentProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { details, dealershipGroups } = userStore();
    const { getPlaylistsRequest, getPlaylist } = playlistStore();
    const { refreshCoreData, setLoading, setPageName } = generalStore();
    const [isEditingPlaylist, setIsEditingPlaylist] = useState(false);
    const [isEditingGroups, setIsEditingGroups] = useState(false);

    // Market responsible & Brand responsible should be able to edit all playlists within their country(s)
    // Dealers should only be able to edit own playlists
    const [isResponsible, setIsResponsible] = useState(false);
    const [isEditable, setIsEditable] = useState(false);

    const [isEditingFallback, setIisEditingFallback] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
    const [isGroupsLoading, setIsGroupsLoading] = useState(true);
    const [playlistDealerGroups, setPlaylistDealerGroups] = useState<DealershipGroupLabel[]>([]);
    const [expandedCountryGroups, setExpandedCountryGroups] = useState<Set<number>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [isFetchingDevicesInfo, setIsFetchingDevicesInfo] = useState(true);
    const [isDeletingPlaylist, setIsDeletingPlaylist] = useState(false);
    const [playlistInfo, setPlaylistInfo] = useState(playlist);
    const [playlistModuleTypesTitles, setPlaylistModuleTypesTitles] = useState<string[]>(playlist.moduleTypes.map(playlistModuleType => {
                const fullModuleType = moduleTypes.find(
                    moduleType =>
                        moduleType[RESPONSE.ID] === playlistModuleType
                );
                return fullModuleType ? fullModuleType.title : '';
            }));
    /**
     * We only want to display dealerships of the playlist which are also assigned to user.
     * Thus, we use /users/me/dealerships endpoint for this purpose.
     * We take playlist's dealership ids and sent these to API.
     * Then using the received values.
     * If the response array is empty, it means that none of the playlist's dealerships are assigned to user
     */
 
    const [devicesPersisted, setDevicesPersisted] = useState<Device[]>([]);

    const [devicesToSet, setDevicesToSet] = useState<Device[]>([]);

    const [showDevicesHint, setShowDevicesHint] = useState(false);

    const controllerRef = useRef<AbortController | null>(null);

    const handleEditPlaylist = () => setIsEditingPlaylist(true);

    const handleEditGroups = () => setIsEditingGroups(true);

    const handleCloseEditGroups = () => setIsEditingGroups(false);

    const handleEditFallback = () => setIisEditingFallback(true);

    const handleCloseEditFallback = () => setIisEditingFallback(false);

    const handleCloseEditModal = () => setIsEditingPlaylist(false);

    const handleModifyDevices = () => setIsDeviceModalOpen(true);

    const handleDeleteModalOpen = () => setIsDeleteModalOpen(true);

    const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

    const handleDuplication = async () => {
        setLoading(true);

        const options = {
            body: {
                title: `[Duplicate] ${playlist.title}`,
                description: playlistInfo.description,
                moduleTypes: playlistInfo.moduleTypes,
                dealershipGroups: playlistInfo.dealershipGroups
            }
        };

        const createdPlaylist = await api.createPlaylist(options);
        const uuid = createdPlaylist[RESPONSE.ID].split('/')[6];

        if (isNotEmpty(playlistInfo.items)) {
            const distributedMedia = playlistInfo.items.map(
                item => item.medium
            );
            await api.addManyItemsToPlaylist(
                createdPlaylist[RESPONSE.ID],
                distributedMedia
            );
        }

        navigate(`${ROUTES.PLAYLISTS}/${uuid}`);
    };

    const handleDeletePlaylistClick = () => {
        setIsDeletingPlaylist(true);
        const url = `${Server.baseUrl}${playlist[RESPONSE.ID]}`;
        apiDelete(url).then(() => {
            setTimeout(async () => {
                await getPlaylistsRequest();
                setIsDeletingPlaylist(false);
                setIsDeleteModalOpen(false);
                navigate(ROUTES.PLAYLISTS);
            }, 1500);
        });
    };

    const handleSetDevices = (deviceIris) => {
        setDevicesToSet(deviceIris);
    };

    const handleRemoveDevice = async (deviceId) => {
        setIsFetchingDevicesInfo(true);

        const url = `${Server.baseUrl}${deviceId}`;

        const devicePromise = await apiGet<ResourceResponse<Device>>(url)

        let playlists: any[] = [];

        if (isNotEmpty(devicePromise.playlists)) {
            playlists = devicePromise.playlists;
        }

        playlists = playlists.filter(
            item => item.contentPlaylist !== playlist[RESPONSE.ID]
        );

        const options = {
                body: {
                    playlists,
                }
            };

        await apiPatch<ResourceResponse<Device>>(url, options);

        await getPlaylist(playlistId, {
            isBackgroundRequest: true
        });

        setIsFetchingDevicesInfo(false);
    }

    const handleSaveDevices = async () => {
        setIsSaving(true);
        /**
         * devicePromises is for getting detailed information of the device.
         * THis is needed for patching/putting playlists of the device
         */
        const devicePromises: Promise<Device>[] = [];

        const userDevices = devicesPersisted.map(getIri);

        const devicesToAdd = devicesToSet.filter(
            iri => !userDevices.includes(iri)
        );

        const devicesToRemove = userDevices.filter(
            // @ts-ignore
            device => !devicesToSet.includes(device)
        );

        for (let i = 0; i < devicesToAdd.length; i++) {
            const url = `${Server.baseUrl}${devicesToAdd[i]}`;
            devicePromises.push(apiGet<ResourceResponse<Device>>(url));
        }

        for (let i = 0; i < devicesToRemove.length; i++) {
            const url = `${Server.baseUrl}${devicesToRemove[i]}`;
            devicePromises.push(apiGet<ResourceResponse<Device>>(url));
        }
        Promise.all(devicePromises).then(responses => {
            const putPromises: Promise<Device>[] = [];

            responses.forEach(response => {
                const url = `${Server.baseUrl}${response[RESPONSE.ID]}`;

                let playlists: any[] = [];

                if (isNotEmpty(response.playlists)) {
                    playlists = response.playlists.map(playlist => ({ contentPlaylist: playlist.contentPlaylist, position: playlist.position }));
                }
                if (!devicesToSet.includes(response[RESPONSE.ID])) {
                    playlists = playlists.filter(
                        item => item.contentPlaylist !== playlist[RESPONSE.ID]
                    );
                } else {
                    playlists.push({ contentPlaylist: playlist[RESPONSE.ID], position: playlists.length + 1 });
                }

                const options = {
                    body: {
                        title: response.title,
                        description: response.description,
                        dealership: response.dealership as any,
                        playlists,
                        deviceClass: response.deviceClass,
                        moduleTypes: response.moduleTypes
                    }
                };
                putPromises.push(
                    apiPatch<ResourceResponse<Device>>(url, options)
                );
            });
            Promise.all(putPromises).then(async () => {
                await getPlaylist(playlistId, {
                    isBackgroundRequest: true
                });

                setIsSaving(false);
                setIsDeviceModalOpen(false);

                refreshCoreData({ isBackgroundRequest: true });
            });
        });
    };

    const handleCancelEditingDevices = () => {
        setIsDeviceModalOpen(false);
        setDevicesPersisted(devicesPersisted);
    };

    /**
     * Playlist can be enabled for multiple dealerships.
     * Some of these dealerships might not be assigned to the current user.
     *
     * some devices are not shared between the playlist dealerships and the user dealerships.
     * i.e those user should see in the view. To recap, users should not see dealerships which they dont have access.
     * Same applies to devices, as devices are bound to dealers.
     *
     * For this reason, we filter out playlist devices that do not belong to user dealerships
     *
     * Returns playlist devices which belongs to user dealerships
     */

    const getPlaylistDevicesInfo = async () => {
        if (isFetchingDevicesInfo) {
            controllerRef.current?.abort();
        }

        setIsFetchingDevicesInfo(true);

        controllerRef.current = new AbortController();
        const signal = controllerRef.current?.signal;
        try {
            // first get all user dealerships
            let nextDealerships: any = [];

            const params = {
                brand: details.userGrantedBrands,
                hasDevices: true,
                pageSize: 300
            };

            const result = await api.user.getAllUserDealerships({
                params,
                signal
            });

            nextDealerships = result;

            // second get the shared devices between user and playlist
            const playlistUserDevices = nextDealerships.reduce(
                (accumulatedDevices: string[], dealership: Dealership) => {
                    return accumulatedDevices.concat([...dealership.devices]);
                },
                []
            );

            const playlistDevices = playlist.devices;

            playlistDevices.forEach(device =>
                playlistUserDevices.includes(device[RESPONSE.ID]) ? device.isUserDevice = true : device.isUserDevice = false
            )

            setShowDevicesHint(playlistDevices.some(device => !device.isUserDevice));
            setDevicesPersisted(playlistDevices);
            setDevicesToSet(playlistDevices);
            setIsFetchingDevicesInfo(false);
        } catch (e) {
            setIsFetchingDevicesInfo(false);
            return '';
        }
    };

    const handleGoToDevice = (device: Device) => {
        navigate(`${ROUTES.DEALERSHIPS}${ROUTES.DEVICES}/${device[RESPONSE.ID].split('/').pop()}?brand=${device[RESPONSE.ID].split('/')[2]}`);
    };

    const classifyDevicesByDealership = (devices) => {
        const uniqueDevices = devices.map(device => ({ dealer: device.dealership?.split('/').pop(), device: device }));

        const grouped = uniqueDevices.reduce((acc, { dealer, device }) => {
            acc[dealer] = acc[dealer] || [];
            acc[dealer].push(device);
            return acc;
        }, {});

        return Object.entries(grouped).map(([dealer, devices]) => ({
            dealer,
            devices
        }));

    };

    const renderDevices = 
        (viewableDevices: [] | Device[]) => {
            if (isNotEmpty(viewableDevices)) {
                return (
                    <>
                        {
                            classifyDevicesByDealership(viewableDevices).map((dealer, index) =>
                                // @ts-ignore
                                <Accordion name={`playlist-devices-dealer-${index + 1}`} key={dealer.dealer} title={`${dealer.dealer} (${dealer.devices.length}  ${t('playlist_device')})`}>
                                    {(dealer.devices as Device[]).map((device, i) => (
                                        <div className={classnames('device-list-item',{ 'is-disabled': !device.isUserDevice })}
                                            key={device[RESPONSE.ID]}>
                                            <p
                                                onClick={() => handleGoToDevice(device)}
                                                data-testid={`playlist-devices-dealer-${index + 1}-device-${i + 1}`}

                                            >
                                                {`${device[RESPONSE.ID].split('/').pop()} - ${
                                                    device.title
                                                }`}
                                            </p>
                                            {device.isUserDevice && <button
                                                className="remove-device-button"
                                                data-testid={`remove-playlist-devices-dealer-${index + 1}-device-${i + 1}`}
                                                onClick={() => handleRemoveDevice(device[RESPONSE.ID])}
                                            >
                                                X
                                            </button>}
                                        </div>
                                    ))}
                                </Accordion>
                            )
                        }
                    </>
                );
            }

            return <span className="no-data">{t('playlist_no_devices')}</span>;
        }

    const renderCreatorRoles = () =>
        playlist.creatorRoles.map(ele => {
            const role = RolesOptions.find(role => role.value === ele);
            return role?.label;
        }).filter(Boolean).join(', ')

    const getDealershipGroups = async() =>{
        setIsGroupsLoading(true);
        const userDealershipGroups = await api.user.getAllDealershipGroups(playlist.dealershipGroups) as any;
        const groupsLabels = userDealershipGroups.map(dealershipGroup =>
            getDealershipGroupLabel(dealershipGroup, t('dealership_group_country_prefix'))
        )
        setPlaylistDealerGroups(groupsLabels)
        setExpandedCountryGroups(new Set());
        setIsGroupsLoading(false);
    };

    const toggleCountryGroupExpanded = (index: number) => {
        setExpandedCountryGroups(previous => {
            const next = new Set(previous);

            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }

            return next;
        });
    };

    const renderFallback = () =>
        isEditingFallback ? (
            <EditFallback
                fallbackCountries={playlistInfo.fallbackCountries}
                onCancel={handleCloseEditFallback}
                playlist={playlistInfo}
                onSuccess={handleEditFallbackSuccess}
            />
        ) : (
            <span
                className={classnames({
                    'no-data':
                        playlistInfo.fallbackCountries.length === 0 &&
                        !playlistInfo.globalFallback
                })}
            >
                <FallbackCountries
                    isGlobalFallback={playlistInfo.globalFallback}
                    fallbackCountries={playlistInfo.fallbackCountries}
                />
            </span>
        );

    const handleSuccessfulPlaylistEditing = () => {
        getPlaylist(playlistId);
        setIsEditingPlaylist(false);
    };

    const handleEditFallbackSuccess = () => {
        refreshCoreData({ isBackgroundRequest: true });
        setIisEditingFallback(false);
    };

    const {
        items,
        description,
        duration,
        title,
        loading,
        creatorRoles
    } = playlistInfo;

    useEffect(() => {
        return () => {
            setPageName('');
            controllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        setPageName(title);
        setPlaylistInfo(playlist);
        getDealershipGroups();
        getPlaylistDevicesInfo();
    }, [playlist]);

    useEffect(() => {
        setIsEditable(details.brandResponsible || details.marketResponsible || playlist.createdBy === details[RESPONSE.ID]);
        setIsResponsible(details.brandResponsible || details.marketResponsible);
    }, [details]);

    return (
        <div className="b-playlistContent">
            <div className='back-button-wrapper'>
                <GoBackButton />
                <div className='playlist-actions'>
                    <Link
                        to={ROUTES.MEDIA_LIBRARY}
                        className={classnames('b-playlist-items-list__go-media-library', {
                            disabled: !isEditable
                        })}
                        data-testid='add-items'

                    >
                        <IconButton src={addIconPath} />
                        {t('playlist_add_items')}
                    </Link>
                    <button
                        className={classnames('b-playlist-items-list__duplicate', {})}
                        onClick={handleDuplication}
                        data-testid='duplicate-playlist'
                    >
                        <IconButton src={duplicateIconPath} />
                        {t('playlist_duplicate')}
                    </button>
                    <button
                        className={classnames('b-playlist-items-list__assign', {})}
                        onClick={handleModifyDevices}
                        data-testid='assign-devices'
                    >
                        <IconButton src={assignIconPath} />
                        {t('playlist_assign')}
                    </button>
                    <button
                        className={classnames('b-playlist-items-list__delete-button', {
                            disabled: !isEditable
                        })}
                        onClick={handleDeleteModalOpen}
                        data-testid='delete-playlist'
                    >
                        <IconButton src={deleteIconPath} />
                        {t('playlist_delete')}
                    </button>
                </div>
            </div>
            <div className='playlist-header'>
                <div>
                    <h3>{title}</h3>
                </div>
                <IconButton onClick={handleEditPlaylist} icon="edit" name="edit-playlist" />
            </div>
            <div className='playlist-info'>
                {isEditingPlaylist ? (
                    <EditPlaylist
                        user={details}
                        moduleTypes={moduleTypes}
                        playlist={playlist}
                        devices={devicesPersisted}
                        onCancel={handleCloseEditModal}
                        onSuccess={handleSuccessfulPlaylistEditing}
                    />
                ) : (
                    <>
                        <div className='info'>
                            <h4>{t('title')}</h4>
                            <p>{title}</p>
                        </div>
                        <div className='info'>
                            <h4>{t('playlist_content')}</h4>
                            <p
                                className={` ${
                                    description === '' ? 'no-data' : ''
                                }`}
                            >
                                {description !== ''
                                    ? description
                                    : t('playlist_no_desc')}
                            </p>
                        </div>
                        <div className='info'>
                            <h4 >{t('playlist_module')}</h4>
                            <div>
                                {playlist.moduleTypes.length > 0 ? (
                                    playlistModuleTypesTitles.join(', ')
                                ) : (
                                    <span className="no-data">{t('edit_playlist_module_type_hint')}</span>
                                )}
                            </div>
                        </div>
                        <div className='info'>
                            <h4>{t('playlist_creator_role')}</h4>
                            <p>{creatorRoles.length > 0 ? renderCreatorRoles() : t('not_available')}</p>
                        </div>
                    </>
                )}
            </div>       
            <div className='playlist-options-header'>
                <h3>{t('playlist_options')}</h3>
            </div>
            <div className='playlist-options'>
                <>
                    {isEditingGroups ? (
                        <>
                            <div className={'info'}>
                                <h4>{t('available_for_edit')}</h4>
                                <EditPlaylistDealershipGroups
                                    dealershipGroups={dealershipGroups}
                                    playlist={playlist}
                                    onCancel={handleCloseEditGroups}
                                    onSuccess={handleSuccessfulPlaylistEditing}
                                />
                            </div>
                        </>
                    ) :
                    (<div className={'info'}>
                        <h4>{t('available_for')}</h4>
                        <div className="dealership-groups-list">
                            {isGroupsLoading ? (
                                <Loader isSmall isInline />
                            ) : playlist.dealershipGroups.length > 0 ? (
                                playlistDealerGroups.map((group, index) =>
                                    group.isCountryGroup ? (
                                        <span className="country-chip-group" key={index}>
                                            {(expandedCountryGroups.has(index)
                                                ? [...group.countries, ...group.overflowCountries]
                                                : group.countries
                                            ).map(country => (
                                                <span
                                                    className="country-chip"
                                                    key={country.code}
                                                    title={country.name}
                                                >
                                                    {country.code}
                                                </span>
                                            ))}
                                            {group.overflowCount > 0 && (
                                                <button
                                                    type="button"
                                                    className="country-chip country-chip-toggle"
                                                    onClick={() => toggleCountryGroupExpanded(index)}
                                                >
                                                    {expandedCountryGroups.has(index)
                                                        ? t('show_less')
                                                        : `+${group.overflowCount}`}
                                                </button>
                                            )}
                                        </span>
                                    ) : (
                                        <span key={index}>
                                            {group.label}
                                            {index < playlistDealerGroups.length - 1 ? ', ' : ''}
                                        </span>
                                    )
                                )
                            ) : (
                                <span className="no-data">
                                    {t('visibility_rules_no_selection')}: {t('visibility_rules_no_selection_result')}
                                </span>
                            )
                            }
                        </div>
                        <IconButton
                            onClick={handleEditGroups}
                            icon="dealership"
                            className="icon"
                            disabled={!isEditable}
                            name="edit-groups"
                        />
                    </div>)
                    }
                </>
                {isResponsible  && (
                    <div className='info'>
                        <h4>{t('playlist_fallback_title')}</h4>
                        <div className="edit-fallback">
                            {renderFallback()}
                        </div>
                        {!isEditingFallback && (
                            <IconButton
                                onClick={handleEditFallback}
                                icon="web-large"
                                className="icon"
                                name="edit-fallback"
                            />
                        )}
                    </div>
                )}
            </div>
            <div className='playlist-devices-header'>
                <h3>{t('playlist_device')}</h3>
                <p className='devices-count' data-testid='playlist-devices-count'>{`${devicesPersisted.length} ${t('playlist_device')}`}</p>
            </div>
            <div className ='playlist-devices'>
                {devicesPersisted.length > 0 && showDevicesHint && <p>* {t('playlist_devices_hint')}</p>}
                <div className="devices">
                    {isFetchingDevicesInfo ? (
                        <Loader isInline isSmall />
                    ) : (
                        renderDevices(devicesPersisted)
                    )}
                </div>
            </div>
            <div className='playlist-elements-header'>
                <h3>{t('playlist_elements')}</h3>
                <p className='duration' data-testid='playlist-duration'>{`${Helpers.humanDuration(duration)} Min`}</p>
            </div>
            <div className='playlist-elements'>
                <PlaylistItemsList
                    setIsEditingPlaylist={setIsEditingPlaylist}
                    loading={loading}
                    playlistId={playlistId}
                    items={items}
                    isEditable={isEditable}
                />
            </div>
            {isDeviceModalOpen && (
                <DeviceAssignmentModal
                    isForPlaylistGroup={false}
                    playlist={playlist}
                    isSaving={isSaving}
                    onClose={handleCancelEditingDevices}
                    onCancel={handleCancelEditingDevices}
                    onAction={handleSaveDevices}
                    onSelect={handleSetDevices}
                    defaultValue={devicesPersisted}
                    playlistModuleTypesTitles={playlistModuleTypesTitles}
                />
            )}
            <DeleteConfirmationModal
                show={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onCancel={handleCloseDeleteModal}
                onAction={handleDeletePlaylistClick}
                isLoading={isDeletingPlaylist}
            >
                <h4>{t('delete_confirm')}</h4>
            </DeleteConfirmationModal>
        </div>
    );
};

export default PlaylistContent;